import { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';

import { API_BASE_URL } from '../config/api';

function StripePaymentForm({ amount, orderData, onSuccess, onError, onProcessingChange, disabled = false }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Notify parent when processing state changes
  useEffect(() => {
    if (onProcessingChange) {
      onProcessingChange(processing);
    }
  }, [processing, onProcessingChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Save orderData to localStorage before redirect (for Amazon Pay and other redirect-based payments)
      // This ensures we can retrieve it on the success page if redirect happens
      if (orderData) {
        localStorage.setItem('checkoutOrderData', JSON.stringify(orderData));
      }

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        console.error('Stripe payment error:', stripeError);
        setError(stripeError.message);
        setProcessing(false);
        onError?.(stripeError);
        return;
      }

      // If paymentIntent is returned and succeeded, confirm immediately
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded, confirming order with backend...', { paymentIntentId: paymentIntent.id });
        // Confirm payment with our backend
        try {
          // Include admin token if logged in (for test mode)
          const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          
          const response = await axios.post(`${API_BASE_URL}/payment/confirm`, {
            paymentIntentId: paymentIntent.id,
            orderData: orderData
          }, { headers });

          console.log('Backend confirmation response:', response.data);
          if (response.data.success) {
            onSuccess?.(response.data);
          } else {
            console.error('Backend confirmation failed:', response.data);
            setError('Payment succeeded but order confirmation failed');
            onError?.(new Error('Order confirmation failed'));
          }
        } catch (err) {
          console.error('Error confirming payment with backend:', err);
          console.error('Error details:', {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status
          });
          setError('Payment succeeded but order confirmation failed');
          onError?.(err);
        }
      } else if (paymentIntent) {
        // Payment intent exists but status is not 'succeeded'
        console.warn('Payment intent status is not succeeded:', {
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status
        });
        setError(`Payment status: ${paymentIntent.status}. Please contact support if payment was charged.`);
        onError?.(new Error(`Payment status: ${paymentIntent.status}`));
      } else {
        // No paymentIntent returned - this might be a redirect-based payment
        // The redirect will happen and CheckoutSuccessPage will handle confirmation
        console.log('No paymentIntent returned - likely redirect-based payment. CheckoutSuccessPage will handle confirmation.');
        // Don't show error - let the redirect happen
        // The success page will handle the confirmation
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-sm text-gray-600">Loading payment form...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing || disabled}
        className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? 'Processing Payment...' : `Pay $${amount.toFixed(2)}`}
      </button>
    </form>
  );
}

export default StripePaymentForm;

